// This helper format form values to be compatible with BE.
// When adding new fields to the form please see reference in issueCreateForm/index.js > getIssueAttributes()
// TO-DO - once we have all fields - combine logic here with serializeRfi in RFIsAPI.ts
import isEmpty from 'lodash/isEmpty';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import { RfiStatus } from '../../sdk';
import { removeEmptyKeys } from '../../helpers';

export const CREATE_ATTRIBUTES_THAT_SHOULD_NOT_BE_EMPTY = [
  ISSUE_ATTRIBUTE_NAMES.STATUS,
  ISSUE_ATTRIBUTE_NAMES.TITLE,
  ISSUE_ATTRIBUTE_NAMES.QUESTION,
  ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER,
  ISSUE_ATTRIBUTE_NAMES.LOCATION_IDS,
  ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION,
];

// Standard fields whose payload key is identical to the Formik values key.
// Each is unconditionally included unless `manageFields` flags it hidden.
const STANDARD_PAYLOAD_FIELDS = [
  ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
  ISSUE_ATTRIBUTE_NAMES.PRIORITY,
  ISSUE_ATTRIBUTE_NAMES.COST,
  ISSUE_ATTRIBUTE_NAMES.SCHEDULE,
  ISSUE_ATTRIBUTE_NAMES.DISCIPLINE,
  ISSUE_ATTRIBUTE_NAMES.CATEGORY,
  ISSUE_ATTRIBUTE_NAMES.SUB_ID,
  ISSUE_ATTRIBUTE_NAMES.WATCHERS,
] as const;

const pickVisibleStandardFields = (
  values: Record<string, unknown>,
  isHidden: (name: string) => boolean,
): Record<string, unknown> =>
  Object.fromEntries(
    STANDARD_PAYLOAD_FIELDS.filter((name) => !isHidden(name)).map((name) => [name, values[name]]),
  );

const pickNeverEmptyFields = (
  values: Record<string, unknown>,
  isHidden: (name: string) => boolean,
): Record<string, unknown> =>
  Object.fromEntries(
    CREATE_ATTRIBUTES_THAT_SHOULD_NOT_BE_EMPTY.filter(
      (name) => !isHidden(name) && !isEmpty(values[name]),
    ).map((name) => [name, values[name]]),
  );

const pickVisibleCustomAttributes = (
  customAttributes: Array<{ id: string }> | undefined,
  hiddenCustomIds: string[],
): Array<{ id: string }> | undefined => {
  if (!customAttributes?.length) return undefined;
  if (hiddenCustomIds.length === 0) return customAttributes;
  const hiddenSet = new Set(hiddenCustomIds);
  const filtered = customAttributes.filter((attr) => !hiddenSet.has(attr.id));
  return filtered.length ? filtered : undefined;
};

const formatFormValuesForSubmission = (values: any, meta: any = {}) => {
  const { status, assignedTo, customIdentifier, rfiTypeId, coReviewers, customAttributes } = values;
  const {
    isRfiCustomIdentifierVisible,
    isDisplayCoReviewers,
    // Defense-in-depth — even if a hidden field's Formik value somehow
    // survived `clearHiddenFieldValues`, drop it from the payload here so it
    // can never reach the BE.
    hiddenStandardNames = [] as string[],
    hiddenCustomIds = [] as string[],
  } = meta;
  const hiddenStandardSet = new Set<string>(hiddenStandardNames);
  const isHiddenStandard = (name: string): boolean => hiddenStandardSet.has(name);

  const isNonDraft = status !== RfiStatus.Draft;
  const visibleCustomAttributes = pickVisibleCustomAttributes(customAttributes, hiddenCustomIds);
  const includeCoReviewers =
    !!isDisplayCoReviewers &&
    !!coReviewers?.length &&
    !isHiddenStandard(ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS);

  const rfiAttributes: Record<string, any> = {
    ...pickVisibleStandardFields(values, isHiddenStandard),
    ...pickNeverEmptyFields(values, isHiddenStandard),
    ...(isNonDraft && rfiTypeId && { [ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID]: rfiTypeId }),
    ...(isRfiCustomIdentifierVisible && { reserveCustomIdentifier: customIdentifier.trim() }),
    ...(isNonDraft && { assignedTo }),
    ...(includeCoReviewers && { coReviewers }),
    ...(visibleCustomAttributes && { customAttributes: visibleCustomAttributes }),
  };

  return removeEmptyKeys(rfiAttributes);
};

export default formatFormValuesForSubmission;
